import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import useApi from 'vl-common/src/hooks/useApi';
import { TimelineTaskQueryParamProps } from './useTimelineTasks';
import { isPast } from 'vl-common/src/lib/isPast';
import { Datestamp } from 'vl-common/src/components/Datestamp';
import useIsReferrerAdmin from 'vl-common/src/hooks/useIsReferrerAdmin';
import useMediaQuery from 'vl-common/src/hooks/useMediaQuery';
import useTransformColumns from 'vl-common/src/hooks/useTransformColumns';
import { HeaderItemType } from 'vl-common/src/components/Table/TableDataView';
import { useIsClientAdmin } from 'vl-common/src/hooks/useIsClientAdmin';
import { Task } from 'vl-common/src/types';

interface SpecialistCategoryProps {
  cat_code: string;
  cat_name: string;
}

interface TaskTypesProps {
  type_code: string;
  task_type_desc: string;
}

const useTimelineTasksTable = () => {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const isClientAdmin = useIsClientAdmin();
  const isReferrerAdmin = useIsReferrerAdmin();

  const [selectTask, setSelectTask] = useState<Task | undefined>(undefined);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showViewTaskModal, setShowViewTaskModal] = useState(false);
  const [cancelTaskModal, setCancelTaskModal] = useState(false);

  const [queryUpdated, setQueryUpdated] = useState(false);

  const showResponsiveColumns = useMediaQuery('(max-width: 1920px)');
  const defaultTaskQueryParams = {
    sort: 'due_date',
    order: 'asc',
    restrict_mode: 'A',
    page: 1
  };
  const [taskQueryParams, setTaskQueryParam] = useState<TimelineTaskQueryParamProps>(() => defaultTaskQueryParams);

  const { data: specialityList } = useQuery({
    queryKey: ['specialityList'],
    queryFn: () => Api.admin.getSpecialistCategory({ rows: 999 })
  });

  const { data: tasksMeta } = useQuery({
    queryKey: ['tasksMeta'],
    queryFn: () => Api.meta.getTaskMeta()
  });

  const taskHeaders: HeaderItemType[] = useMemo(
    () => [
      {
        display: 'Task ID',
        dataSourceKey: 'task_id',
        isSortable: true
      },
      {
        display: 'Specialty',
        dataSourceKey: 'cat_desc',
        isSortable: false,
        filterMultiple: false,
        responsive: ['xl'],
        filterKey: 'spec_code',
        filters: specialityList?.data.cats.map((item: SpecialistCategoryProps) => ({
          value: item.cat_code,
          text: item.cat_name
        }))
      },
      {
        display: 'Task Type',
        dataSourceKey: 'task_type',
        filterMultiple: false,
        responsive: ['lg'],
        filterKey: 'task_type',
        filters: tasksMeta?.data.meta[0].task_types.map((item: TaskTypesProps) => ({
          value: item.type_code,
          text: item.task_type_desc
        }))
      },
      {
        display: 'Status',
        dataSourceKey: 'status',
        isSortable: false,
        filterMultiple: false,
        responsive: ['lg'],
        filterKey: 'task_status_code',
        filters: tasksMeta?.data.meta[0].task_stati.map((item: any) => ({
          value: item.task_status_code,
          text: item.task_status_desc
        }))
      },
      {
        display: 'Due Date',
        dataSourceKey: 'due_date',
        filterable: {
          exact: false,
          queryKey: 'date_end',
          dateStartFilterKey: 'date_start',
          dateEndFilterKey: 'date_end'
        },
        dateFilter: true,
        columnDataFormat: ({ data }: { data: any }) => {
          const date = new Date(data);

          return (
            <span style={{ color: isPast(date) ? 'red' : 'black' }}>
              <Datestamp
                date={new Date(data)}
                format={'Date @ Time (short, no year, 24h) - Fri, Mar 3rd @ 08:57'} // LLL is wrong? Your guess is as good as mine
              />
            </span>
          );
        },
        isSortable: true
      },
      {
        display: 'Actions',
        isAction: true,
        actionItems: [
          // Active Action items
          {
            labelFormat: (data: { actionable: number }) => {
              const style = data.actionable === 0 ? { color: 'lightgray' } : {};

              return (
                <span data-testid={'open-edit-assigned-to'} style={style}>
                  {t('Edit assigned to')}
                </span>
              );
            },
            isDisabled: (data) => data.actionable === 0,
            isVisible: (data: { status: string }) => {
              return data.status === 'Open' && isClientAdmin;
            },
            onClick: (data: React.SetStateAction<any>) => {
              if (data.actionable !== 0) {
                setSelectTask(data);
                setShowEditModal(true);
              }
            }
          },
          {
            labelFormat: (data: { actionable: number; task_id: number }) => {
              const style = data.actionable === 0 ? { color: 'lightgray' } : {};

              return (
                <span data-testid={`open-complete-task-${data.task_id}`} style={style}>
                  {t('Complete Task')}
                </span>
              );
            },
            isDisabled: (data) => data.actionable === 0,
            isVisible: (data: { status: string }) => {
              return data.status === 'Open' && isClientAdmin;
            },
            onClick: (data: React.SetStateAction<any>) => {
              if (data.actionable !== 0) {
                setSelectTask(data);
                setShowCompleteTaskModal(true);
              }
            }
          },
          {
            labelFormat: (data: { actionable: number }) => {
              const style = data.actionable === 0 ? { color: 'lightgray' } : {};

              return (
                <span data-testid={'open-cancel-task'} style={style}>
                  {t('Cancel Task')}
                </span>
              );
            },
            isDisabled: (data) => data.actionable === 0,
            isVisible: (data: { status: string }) => {
              return data.status === 'Open' && isClientAdmin;
            },
            onClick: (data: React.SetStateAction<any>) => {
              if (data.actionable !== 0) {
                setSelectTask(data);
                setCancelTaskModal(!cancelTaskModal);
              }
            }
          },
          // Completed Action items
          {
            labelFormat: (data: { actionable: number; task_id: number }) => {
              return <span data-testid={`open-view-task-${data.task_id}`}>{t('View')}</span>;
            },
            onClick: (data: React.SetStateAction<any>) => {
              setSelectTask({ ...data, justView: true });
              setShowViewTaskModal(!showViewTaskModal);
            }
          },
          // All Action items
          {
            isVisible: () => {
              return isClientAdmin;
            },
            labelFormat: (data: { actionable: number }) => {
              return <span data-testid={'open-view-audit-details'}>{t('View Audit Trail')}</span>;
            },
            onClick: (data: React.SetStateAction<any>) => {
              setSelectTask(data);
              setShowAuditModal(true);
            }
          }
        ]
      }
    ],
    [showResponsiveColumns, isReferrerAdmin, specialityList, tasksMeta]
  );

  const expandedColumns = useMemo(
    () => [
      {
        display: 'Task Notes',
        dataSourceKey: 'task_notes'
      },
      {
        display: 'Assigned By',
        dataSourceKey: 'assigned_by'
      },
      {
        display: 'Assigned To',
        dataSourceKey: 'assigned_to'
      },
      {
        display: 'Created By',
        dataSourceKey: 'creator_name'
      },
      {
        display: 'Created Date',
        dataSourceKey: 'created_date',
        columnDataFormat: ({ data: date }: { data: Date }) => (
          <Datestamp date={new Date(date)} format={'Date @ Time (short, no year, 24h) - Fri, Mar 3rd @ 08:57'} />
        )
      }
    ],
    []
  );

  const updateTaskQueryParams = useCallback((params: TimelineTaskQueryParamProps) => {
    setQueryUpdated(true);
    if (params.task_status_code) {
      setTaskQueryParam((prev) => ({
        ...defaultTaskQueryParams,
        ...params,
        active: params.task_status_code?.some((status) => status === 'OPEN')
      }));
    } else {
      setTaskQueryParam((prev) => {
        const newData = { ...defaultTaskQueryParams, ...params };
        delete newData['active'];
        return newData;
      });
    }
  }, []);

  const transformedColumns = useTransformColumns(taskHeaders);

  const useEditModal = () => {
    const [showEditSuccessModal, setShowEditSuccessModal] = useState(false);

    const onCloseEditModal = () => {
      setSelectTask(undefined);
      setShowEditModal(false);
    };

    const onEditSuccess = () => {
      setSelectTask(undefined);
      setShowEditModal(false);
      setShowEditSuccessModal(true);
    };

    const onCloseEditSuccessModal = () => {
      setShowEditSuccessModal(false);
    };

    return {
      showEditModal,
      showEditSuccessModal,
      onCloseEditModal,
      onEditSuccess,
      onCloseEditSuccessModal
    };
  };

  const useCompleteTaskModal = () => {
    const [showCompleteTaskSuccessModal, setShowCompleteTaskSuccessModal] = useState(false);
    const [showSaveCompleteTaskSuccessModal, setShowSaveCompleteTaskSuccessModal] = useState(false);

    const onCloseCompleteTaskModal = () => {
      setSelectTask(undefined);
      setShowCompleteTaskModal(false);
    };

    const onCompleteTaskSuccess = () => {
      setShowCompleteTaskModal(false);
      setShowCompleteTaskSuccessModal(true);
    };

    const onCloseCompleteTaskSuccessModal = () => {
      setSelectTask(undefined);
      setShowCompleteTaskSuccessModal(false);
      setShowSaveCompleteTaskSuccessModal(false);
    };

    const onCompleteSaveTaskSuccess = () => {
      setShowCompleteTaskModal(false);
      setShowSaveCompleteTaskSuccessModal(true);
    };

    return {
      showCompleteTaskModal,
      showCompleteTaskSuccessModal,
      showSaveCompleteTaskSuccessModal,
      onCloseCompleteTaskModal,
      onCompleteTaskSuccess,
      onCloseCompleteTaskSuccessModal,
      onCompleteSaveTaskSuccess
    };
  };

  const useViewModal = () => {
    const onCloseViewTaskModal = () => {
      setSelectTask(undefined);
      setShowViewTaskModal(false);
    };

    return {
      showViewTaskModal,
      onCloseViewTaskModal
    };
  };
  const useTaskAuditModal = () => {
    const onCloseAuditModal = () => {
      setSelectTask(undefined);
      setShowAuditModal(false);
    };

    return {
      showAuditModal,
      onCloseAuditModal
    };
  };

  const useCancelTaskModal = () => {
    const [cancelTaskSuccessModal, setCancelTaskSuccessModal] = useState(false);
    const onCloseCancelTaskModal = () => {
      setSelectTask(undefined);
      setCancelTaskModal(false);
    };

    const onCancelTaskCompleted = () => {
      setCancelTaskModal(false);
      setCancelTaskSuccessModal(true);
    };

    const onCloseCancelTaskSuccessModal = () => {
      setSelectTask(undefined);
      setCancelTaskSuccessModal(false);
    };

    return {
      cancelTaskModal,
      cancelTaskSuccessModal,
      onCloseCancelTaskModal,
      onCancelTaskCompleted,
      onCloseCancelTaskSuccessModal
    };
  };

  return {
    taskQueryParams,
    taskHeaders: transformedColumns,
    expandedColumns,
    updateTaskQueryParams,
    queryUpdated,
    resetQueryUpdated: () => setQueryUpdated(false),
    selectTask,
    useEditModal,
    useCompleteTaskModal,
    useViewModal,
    useTaskAuditModal,
    useCancelTaskModal
  };
};

export default useTimelineTasksTable;
